import type { CashStripPoint, CashTodo, CashWeekly } from '@/services/http/cash'

/**
 * Haftalık Nakit verisinden karar özeti ve şerit noktaları (yalnız gösterim türetmesi). Hiçbir
 * rakam yeniden hesaplanmaz: dönem sonu nakit satırı, minimum nakit eşiği ve sıralama backend'den
 * gelir; burada yalnız en küçüğü bulma, sayma ve fark alma var. Aylık dönemler hariç, yalnız haftalar.
 */

const EPSILON = 0.005

export type CashStatus = 'good' | 'attention' | 'problem'

interface WeekClosing {
  start: string
  end: string
  closing: number
}

function weekClosings(weekly: CashWeekly): WeekClosing[] {
  const row = weekly.rows.find((r) => r.key === 'donem_sonu_nakit')
  if (!row) return []
  return weekly.periods.flatMap((period, i) =>
    period.kind === 'hafta' && row.values[i] !== undefined
      ? [{ start: period.start, end: period.end, closing: row.values[i]! }]
      : [],
  )
}

/** Haftalık ekranın şeridi: seçili senaryonun hafta sonu nakitleri, eşiğin altı işaretli. */
export function stripPoints(weekly: CashWeekly): CashStripPoint[] {
  return weekClosings(weekly).map((w) => ({
    ...w,
    below_min: w.closing >= -EPSILON && w.closing < weekly.min_cash,
  }))
}

export interface CashSummary {
  status: CashStatus
  /** En düşük dönem sonu nakit ve haftası; hafta yoksa `null`. */
  lowest: { closing: number; end: string } | null
  negativeWeeks: number
  belowMinWeeks: number
  /** En düşük nakdin minimum nakit eşiğinin ne kadar altında kaldığı; altına inmiyorsa 0. */
  shortfall: number
  minCash: number
  /** Nakdin eşik altına indiği ilk hafta (backend `first_below_min`). */
  firstBelowMin: string | null
}

export function summarizeWeekly(weekly: CashWeekly): CashSummary {
  const weeks = weekClosings(weekly)
  let lowest: CashSummary['lowest'] = null
  for (const w of weeks) if (!lowest || w.closing < lowest.closing) lowest = w
  const negativeWeeks = weeks.filter((w) => w.closing < -EPSILON).length
  const belowMinWeeks = weeks.filter(
    (w) => w.closing >= -EPSILON && w.closing < weekly.min_cash,
  ).length
  return {
    status: negativeWeeks > 0 ? 'problem' : belowMinWeeks > 0 ? 'attention' : 'good',
    lowest: lowest && { closing: lowest.closing, end: lowest.end },
    negativeWeeks,
    belowMinWeeks,
    shortfall: lowest ? Math.max(0, weekly.min_cash - lowest.closing) : 0,
    minCash: weekly.min_cash,
    firstBelowMin: weekly.first_below_min ?? null,
  }
}

export interface ScenarioGap {
  /** Kötümser − baz: en düşük nakit farkı (kötümserde düşükse eksi). */
  lowestDelta: number
  /** Kötümserde ek eksi hafta sayısı. */
  negativeWeeksDelta: number
}

export function scenarioGap(base: CashSummary, pessimistic: CashSummary): ScenarioGap | null {
  if (!base.lowest || !pessimistic.lowest) return null
  return {
    lowestDelta: pessimistic.lowest.closing - base.lowest.closing,
    negativeWeeksDelta: pessimistic.negativeWeeks - base.negativeWeeks,
  }
}

/** Sütun başına durum (tablo başlığındaki görünür işaret için); dönem sonu nakit satırına göre. */
export function periodStatuses(weekly: CashWeekly): ('negative' | 'belowMin' | undefined)[] {
  const row = weekly.rows.find((r) => r.key === 'donem_sonu_nakit')
  return weekly.periods.map((_, i) => {
    const value = row?.values[i]
    if (value === undefined) return undefined
    if (value < -EPSILON) return 'negative'
    return value < weekly.min_cash ? 'belowMin' : undefined
  })
}

/** Bu Hafta maddelerinin gösterim sırası: ödemeyi ya da nakdi en çok etkileyen önce. */
const TODO_ORDER: CashTodo['kind'][] = [
  'eksi_hafta',
  'iban_hata',
  'liste_onay',
  'iban_eksik',
  'gerceklesti_mi',
  'pos_valoru_gecmis',
  'ciro_farki',
  'tanimsiz_kod',
]

export function sortTodo(items: CashTodo[]): CashTodo[] {
  const rank = (kind: CashTodo['kind']) => {
    const i = TODO_ORDER.indexOf(kind)
    return i === -1 ? TODO_ORDER.length : i
  }
  return [...items].sort((a, b) => rank(a.kind) - rank(b.kind))
}

export type TodoTone = 'danger' | 'warning'

/** Eksi hafta maddesi nakit gerçekten eksiye düşüyorsa, hatalı IBAN her zaman kırmızı; gerisi uyarı. */
export function todoTone(item: CashTodo): TodoTone {
  if (item.kind === 'iban_hata') return 'danger'
  if (item.kind === 'eksi_hafta' && (item.amount ?? 0) < -EPSILON) return 'danger'
  return 'warning'
}
